import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Database } from "@/integrations/supabase/types";

type Course = Database["public"]["Tables"]["courses"]["Row"] & {
  instructor?: {
    full_name: string;
    avatar_url: string | null;
  };
  enrollment_count?: number;
  average_rating?: number;
  review_count?: number;
};

type Filters = {
  search: string;
  level: string;
  price: string;
  sort: string;
  featured: boolean;
};

export const useCourses = () => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<Filters>({
    search: "",
    level: "all",
    price: "all",
    sort: "newest",
    featured: false,
  });

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch published courses with instructor data
      const { data: coursesData, error: coursesError } = await supabase
        .from("courses")
        .select(`
          *,
          instructor:profiles!courses_instructor_id_fkey(
            full_name,
            avatar_url
          )
        `)
        .eq("status", "published")
        .order("created_at", { ascending: false });

      if (coursesError) throw coursesError;

      // Fetch enrollment counts
      const { data: enrollmentsData } = await supabase
        .from("enrollments")
        .select("course_id");

      // Fetch average ratings
      const { data: reviewsData } = await supabase
        .from("reviews")
        .select("course_id, rating");

      // Calculate enrollment counts and ratings
      const enrollmentCounts = enrollmentsData?.reduce((acc, enrollment) => {
        acc[enrollment.course_id] = (acc[enrollment.course_id] || 0) + 1;
        return acc;
      }, {} as Record<string, number>) || {};

      const courseRatings = reviewsData?.reduce((acc, review) => {
        if (!acc[review.course_id]) {
          acc[review.course_id] = { total: 0, count: 0 };
        }
        acc[review.course_id].total += review.rating;
        acc[review.course_id].count += 1;
        return acc;
      }, {} as Record<string, { total: number; count: number }>) || {};

      // Combine data
      const enrichedCourses = coursesData?.map((course) => ({
        ...course,
        enrollment_count: enrollmentCounts[course.id] || 0,
        average_rating: courseRatings[course.id]
          ? courseRatings[course.id].total / courseRatings[course.id].count
          : 0,
        review_count: courseRatings[course.id]?.count || 0,
      })) || [];

      setCourses(enrichedCourses);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch courses");
      console.error("Error fetching courses:", err);
    } finally {
      setLoading(false);
    }
  };

  // Client-side filtering and sorting
  const filteredCourses = courses
    .filter((course) => {
      // Search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const titleMatch = course.title.toLowerCase().includes(searchLower);
        const descMatch = course.description?.toLowerCase().includes(searchLower);
        if (!titleMatch && !descMatch) return false;
      }

      // Level filter
      if (filters.level !== "all" && course.level !== filters.level) {
        return false;
      }

      // Price filter
      if (filters.price === "free" && (course.price || 0) > 0) {
        return false;
      }
      if (filters.price === "paid" && (course.price || 0) === 0) {
        return false;
      }

      // Featured filter
      if (filters.featured && !course.is_featured) {
        return false;
      }

      return true;
    })
    .sort((a, b) => {
      // Sorting
      switch (filters.sort) {
        case "popular":
          return (b.enrollment_count || 0) - (a.enrollment_count || 0);
        case "price-low":
          return (a.price || 0) - (b.price || 0);
        case "price-high":
          return (b.price || 0) - (a.price || 0);
        case "newest":
        default:
          return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
      }
    });

  return {
    courses: filteredCourses,
    loading,
    error,
    filters,
    setFilters,
    refetch: fetchCourses,
  };
};
